'use client';

import { type InboxMessage, useInbox } from '@shared/providers';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { RichContent } from '../../../RichContent/RichContent';
import { Cta } from './components/Cta/Cta';
import { OpenReadController } from './components/OpenReadController/OpenReadController';

type InboxDetailedViewProps = {
  id: string;
  children: React.ReactNode;
  className?: string;
};

const InboxDetailedView = ({ id, children, className }: InboxDetailedViewProps) => {
  const t = useTranslations('my_account.inbox.detailed_view');
  const tInbox = useTranslations('my_account.inbox');
  const { messages } = useInbox();
  const messageData = messages.find((message: InboxMessage) => message.id === id);

  if (!messageData) {
    return children;
  }

  const { executionDate, title, media, content, ctaURL, ctaText, readDate } = messageData;

  return (
    <div className={className}>
      <p className="paragraph-s-strong text-foreground-subtle mt-1">{tInbox('date', { date: new Date(executionDate) })}</p>
      <h1 className="heading-4 text-foreground mt-1">{title}</h1>
      {media ? (
        <div className="relative w-full h-36 mt-5 bg-container-active rounded-lg overflow-hidden">
          <Image src={media} alt={title} unoptimized fill className="object-cover rounded-lg" sizes="(max-width: 768px) 100vw, 576px" />
        </div>
      ) : null}
      <RichContent information={content} className="mt-5 inbox-html" />
      {ctaURL ? (
        <Cta id={id} href={ctaURL}>
          {ctaText ?? t('cta')}
        </Cta>
      ) : null}
      <OpenReadController id={id} readDate={readDate} />
    </div>
  );
};

export { InboxDetailedView };
